import { ref, useContext } from '@nuxtjs/composition-api';
import type { Ref } from '@nuxtjs/composition-api';
import { Logger } from '~/helpers/logger';
import { transformUserCreateAddressInput, transformUserUpdateAddressInput } from '~/modules/customer/helpers/userAddressManipulator';
import type { ComposableFunctionArgs } from '~/composables/types';
import { CustomQuery, CustomHeaders } from '~/types/core';
import type { UseAddressesInterface, UseAddressesParamsInput, UseAddressesErrors } from './useAddresses';
import { useClevertapStore } from '~/stores/clevertap';
import { useRegionStore } from '~/stores/extensionApi/region';

/**
 * @public
 *
 * Allows loading and manipulating addresses of the current user. These
 * addresses can be used for both billing and shipping.
 *
 * See the {@link UseAddressesInterface} for a list of methods and values available in this composable.
 */
export function useAddresses(): UseAddressesInterface {
  const error: Ref<UseAddressesErrors> = ref({
    load: null,
    save: null,
    remove: null,
    update: null,
  });
  const loading: Ref<boolean> = ref(false);
  const { app } = useContext();
  const context = app.$vsf;
  const clevertapStore = useClevertapStore();
  const regionStore = useRegionStore();

  const load = async (customQuery?: CustomQuery, customHeaders?: CustomHeaders) => {
    Logger.debug('[Magento] load user addresses');
    let results = null;

    try {
      loading.value = true;
      const { data } = await context.$magento.api.getCustomerAddresses(customQuery, customHeaders);
      results = data?.customer?.addresses ?? [];
      Logger.debug('[Magento] load user addresses results:', results);
      error.value.load = null;
    } catch (err) {
      error.value.load = err;
      Logger.error('Magento] load user addresses error', err);
    } finally {
      loading.value = false;
    }

    return results;
  };

  const save = async (params: ComposableFunctionArgs<UseAddressesParamsInput>) => {
    Logger.debug('[Magento] save user address:', params.address);
    let results = null;

    try {
      loading.value = true;
      const { data } = await context.$magento.api.createCustomerAddress(
        transformUserCreateAddressInput(params),
        params?.customQuery ?? null,
        params?.customHeaders,
      );
      if (!data?.createCustomerAddress) {
        error.value.save = new Error('Failed to save address');
        return null;
      }
      results = data?.createCustomerAddress ?? {};
      Logger.debug('[Magento] save user address results:', params.address);
      error.value.save = null;
      
      // Track new address added with CleverTap
      try {
        if (process.client && typeof window !== 'undefined' && window.clevertap) {
          const addressData = params.address;
          const street = Array.isArray(addressData.street) ? addressData.street[0] : addressData.street;
          
          const regionId = addressData.region?.region_id;
          let regionName = '';
          
          if (regionId) {
            const stateData = regionStore.getStateData("VN");
            if (stateData && stateData.length > 0) {
              const regionId = addressData.region?.region_id;
              const regionMatch = stateData.find(state => state.region_id === regionId);
              if (regionMatch) {
                regionName = regionMatch.default_name || '';
              }
            }
          }
          
          const city = addressData.city || '';
          let wardName = '';
          
          if (addressData.extension_attributes?.ward_name) {
            wardName = addressData.extension_attributes.ward_name;
          } else if (addressData.custom_attributes) {
            const wardAttr = Array.isArray(addressData.custom_attributes) 
              ? addressData.custom_attributes.find(attr => attr.attribute_code === 'ward_name')
              : null;
            if (wardAttr) {
              wardName = wardAttr.value || '';
            }
          }
          const trackingData = {
            checkout_id: context.$magento.config.state.getCartId(),
            address_id: results.id || '',
            full_name: addressData.firstname + (addressData.lastname ? ' ' + addressData.lastname : ''),
            phone_number: addressData.telephone || '',
            city: regionName,
            district: city,
            ward: wardName,
            address: street || ''
          };
          
          clevertapStore.newAddressAddedCleverTap({
            clevertap: window.clevertap,
            data: trackingData
          });
        }
      } catch (err) {
        Logger.error('[Magento] track new address error:', err);
        // Don't throw error for tracking failures
      }
    } catch (err) {
      error.value.save = err;
      Logger.error('[Magento] save user address error:', err);
    } finally {
      loading.value = false;
    }

    return results;
  };

  const update = async (params: ComposableFunctionArgs<UseAddressesParamsInput>) => {
    Logger.debug('[Magento] update user address:', params.address);
    let results = null;
    const addressInput = {
      ...transformUserUpdateAddressInput(params),
      id: params.address.id
    };
    try {
      loading.value = true;
      const { data } = await context.$magento.api.updateCustomerAddress(
        addressInput,
        params?.customQuery ?? null,
        params?.customHeaders,
      );

      if (!data?.updateCustomerAddress) {
        error.value.update = new Error('Failed to save address');
        return null;
      }
      results = data?.updateCustomerAddress ?? {};
      Logger.debug('[Magento] update user address results:', results);
      error.value.update = null;
    } catch (err) {
      error.value.update = err;
      Logger.error('[Magento] update user address error:', err);
    } finally {
      loading.value = false;
    }

    return results;
  };

  const remove = async (params: ComposableFunctionArgs<UseAddressesParamsInput>) => {
    Logger.debug('[Magento] remove user address:', params.address);
    let results = null;

    try {
      loading.value = true;
      const { data } = await context.$magento.api.deleteCustomerAddress(
        params.address.id,
        params?.customQuery ?? null,
        params?.customHeaders,
      );
      results = !!data.deleteCustomerAddress;
      Logger.debug('[Magento] remove user address results:', results);
      error.value.remove = null;
    } catch (err) {
      error.value.remove = err;
      Logger.error('[Magento] remove user address error:', err);
    } finally {
      loading.value = false;
    }

    return results;
  };

  return {
    error,
    loading,
    load,
    save,
    update,
    remove,
  };
}

export * from './useAddresses';
export default useAddresses;
